import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import auth from "services/auth";
import ls from "services/ls";
import AppContext from "contexts/AppContext";
import api from "services/api";

import { Button } from "react-bootstrap";
import { __ } from "services/Translator";

import FacebookLogin from "react-facebook-login";
import config from "configs/config.js";
import jwt_decode from "jwt-decode";
import swal from "sweetalert2";
import { sanitizeTextInput } from "utils/profileValidation";
import { getEnabledSocialProviders } from "utils/signupFlow";

const SocialLogin = (props) => {
  const {
    bonusOffer,
    referralCode,
    variant = "classic",
    googleButtonId = "googleLoginModal",
    showOrDivider = true,
  } = props;

  const history = useHistory();
  const { updateUserAuthStatus } = React.useContext(AppContext);
  const [loginData, setLoginData] = useState({});

  const buildSocialSignupPayload = (data) => {
    const payload = {
      ...data,
      agreeToTerms: data.agreeToTerms ? 1 : 0,
      acceptCountryLimitation: data.acceptCountryLimitation ? 1 : 0,
    };

    if (bonusOffer) {
      payload.bonusOffer = bonusOffer;
    }

    const sanitizedReferral = sanitizeTextInput(referralCode, 80);
    if (sanitizedReferral) {
      payload.referralCode = sanitizedReferral;
    }

    const affToken = ls.get("afftoken");
    if (affToken) {
      payload.affiliateToken = affToken;
    }

    return payload;
  };

  const socialLoginAction = async (loginData) => {
    var response = await api.post("/social-login", loginData);
    if (response.data.status == "success") {
      await auth.login(response.data.user);
      updateUserAuthStatus(response.data.user);
      // Land on the same destination as email/password + email-OTP auth.
      history.push("/games/all");
    } else {
      return new swal({
        className: "custom_swal",
        title: __("Failed!"),
        text: response.data.message,
      });
    }
  };

  // Prefer settings supplied by the parent (avoids a duplicate fetch and the
  // brief flash where providers render before the brand config arrives). Fall
  // back to a self-fetch for callers that do not pass them (e.g. banner login).
  const [fetchedSettings, setFetchedSettings] = useState(null);
  const initialSettings = props.initialSettings || fetchedSettings || {};

  const getInitialSettings = async () => {
    var response = await api.get("/initial-settings");
    setFetchedSettings(response.data);
  };

  useEffect(() => {
    if (!props.initialSettings) {
      getInitialSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialSettings]);

  const [spinner, setSpinner] = useState(false);
  const [showSocialRegTerms, setShowSocialRegTerms] = useState(false);
  const [socialRegisterData, setSocialRegisterData] = useState({
    name: "",
    email: "",
    socialId: "",
    provider: "",
    agreeToTerms: 0,
    acceptCountryLimitation: 0,
  });

  const responseFacebook = (response) => {
    setSocialRegisterData({
      name: response.name,
      email: response.email,
      socialId: response.userID,
      provider: "Facebook",
      agreeToTerms: 0,
      acceptCountryLimitation: 0,
    });
    setRegisterData({
      name: response.name,
      email: response.email,
      socialId: response.userID,
      provider: "Facebook",
      agreeToTerms: 0,
      acceptCountryLimitation: 0,
    });
  };

  const componentClicked = () => {
    setSpinner(true);
  };

  const responseGoogle = (response) => {
    var userObject = jwt_decode(response.credential);
    setSocialRegisterData({
      name: userObject.name,
      email: userObject.email,
      socialId: userObject.sub,
      provider: "Google",
      agreeToTerms: 0,
    });

    setRegisterData({
      name: userObject.name,
      email: userObject.email,
      socialId: userObject.sub,
      provider: "Google",
      agreeToTerms: 0,
    });
    props.setSpinner?.(false);
  };

  useEffect(() => {
    if (!window.google || props.callFrom !== "modal") return;

    window.google.accounts.id.initialize({
      client_id: config.GoogleClientId,
      callback: responseGoogle,
    });

    const mount = document.getElementById(googleButtonId);
    if (mount) {
      mount.innerHTML = "";
      window.google.accounts.id.renderButton(mount, {
        theme: "outline",
        type: "icon",
        shape: "circle",
        size: "small",
      });
    }
  }, [googleButtonId, props.callFrom, variant]);

  const triggerGoogleLogin = () => {
    window.google.accounts.id.prompt();
  };

  const [socialLoginData, setSocialLoginData] = useState({});
  const [applyLogin, setApplyLogin] = useState(false);

  const checkAvailability = async () => {
    if (socialRegisterData.email.length > 0) {
      var response = await api.post("/check-social-availability", socialRegisterData);

      if (response.data.status == "Not Found") {
        setShowSocialRegTerms(true);
      } else if (
        response.data.status == "Already Registered" ||
        response.data.status == "Email Already Registered"
      ) {
        setSocialLoginData({
          email: socialRegisterData.email,
          socialId: socialRegisterData.socialId,
          provider: socialRegisterData.provider,
        });

        setLoginData({
          email: socialRegisterData.email,
          socialId: socialRegisterData.socialId,
          provider: socialRegisterData.provider,
        });
        socialLoginAction({
          email: socialRegisterData.email,
          socialId: socialRegisterData.socialId,
          provider: socialRegisterData.provider,
        });
        setApplyLogin(true);
      } else {
        setSpinner(false);
      }
    }
  };

  useEffect(() => {
    if (socialRegisterData.provider != "") {
      checkAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialRegisterData]);

  const [errors, setErrors] = useState({});
  const [registerData, setRegisterData] = useState({
    name: "",
    email: "",
    socialId: "",
    provider: "",
    agreeToTerms: 0,
    acceptCountryLimitation: 0,
  });

  const updateRegisterForm = (field, value) => {
    setRegisterData({ ...registerData, [field]: value });
  };

  const registerInputChange = (ev) => {
    var target = ev.target;
    var field = target.name;
    var value = target.value;

    if (field == "agreeToTerms") {
      value = ev.target.checked ? 1 : 0;
    }
    if (field == "acceptCountryLimitation") {
      value = ev.target.checked ? 1 : 0;
    }

    updateRegisterForm(field, value);
  };

  const registerValidateForm = () => {
    var valid = true;
    var errorsTmp = {};

    if (!registerData.agreeToTerms) {
      errorsTmp = {
        ...errorsTmp,
        agreeToTerms: __("Please read & accept terms & privacy policy"),
      };
      valid = false;
    }

    setErrors(errorsTmp);
    return valid;
  };

  const registrationAction = async (ev) => {
    ev.preventDefault();
    var valid = registerValidateForm();
    if (valid) {
      var response = await api.post("/social-signup", buildSocialSignupPayload(registerData));
      if (response.data.status == "success") {
        setApplyLogin(true);
        setLoginData({
          email: registerData.email,
          socialId: registerData.socialId,
          provider: registerData.provider,
        });
        socialLoginAction({
          email: registerData.email,
          socialId: registerData.socialId,
          provider: registerData.provider,
        });
      } else {
        setErrors(response.data.errors);
      }
    }
  };

  // Registry/config-driven: only providers enabled for this brand render.
  // Falls back to the legacy per-provider flags when signup_flow is absent.
  const enabledProviders = getEnabledSocialProviders(initialSettings);

  const isBetmodeLayout = variant === "betmode" || variant === "streamlined";
  const socialAreaClass =
    "socialLoginArea" +
    (isBetmodeLayout ? " socialLoginArea--betmode" : "");

  const renderBetmodeGoogleIcon = () => (
    <svg className="betmode-auth__social-icon" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );

  return (
    <>
      <div className={socialAreaClass}>
        {!showSocialRegTerms && (
          <>
            {enabledProviders.Google ? (
              <>
                {props.callFrom == "banner" && (
                  <div className="googleLoginBtn">
                    <div id="bannerGoogleLogin" className="googleLoginButton"></div>
                    <div className="clearfix"></div>
                  </div>
                )}
                {props.callFrom == "modal" && isBetmodeLayout ? (
                  <>
                    <button type="button" className="betmode-auth__social-btn" onClick={triggerGoogleLogin}>
                      {renderBetmodeGoogleIcon()}
                      <span>{__("Google")}</span>
                    </button>
                    <div id={googleButtonId} className="betmode-auth__google-mount" aria-hidden="true" />
                  </>
                ) : null}
                {props.callFrom == "modal" && !isBetmodeLayout ? (
                  <div onClick={triggerGoogleLogin} className="googleLoginBtn">
                    <div id={googleButtonId} className="googleLoginButton"></div>
                    <div className="clearfix"></div>
                  </div>
                ) : null}
              </>
            ) : null}
            {enabledProviders.Facebook ? (
              isBetmodeLayout && props.callFrom == "modal" ? (
                <FacebookLogin
                  appId={config.FBAppId}
                  autoLoad={false}
                  fields="name,email,picture"
                  textButton={__("Facebook")}
                  callback={responseFacebook}
                  onClick={componentClicked}
                  cssClass="betmode-auth__social-btn betmode-auth__social-btn--facebook"
                  icon="fab fa-facebook-f betmode-auth__social-icon-fb"
                />
              ) : (
                <div className="facebookLoginBtn">
                  <FacebookLogin
                    appId={config.FBAppId}
                    autoLoad={false}
                    fields="name,email,picture"
                    textButton=""
                    callback={responseFacebook}
                    onClick={componentClicked}
                    cssClass="login_btn facebook_btn"
                    icon="fab fa-facebook facebook_icon"
                  ></FacebookLogin>
                </div>
              )
            ) : null}
          </>
        )}

        {showSocialRegTerms && (
          <div className="registerTermsArea">
            <div className="form_area">
              <div className="common_headline text-center">
                {__("Please agree to the following terms")}
              </div>
              <div className="input_item">
                <div className="reg_check_area">
                  <input
                    type="checkbox"
                    className="stepTerms"
                    id={"socialAgeConfirmation-" + googleButtonId}
                    name="agreeToTerms"
                    onChange={registerInputChange}
                  />
                  <label htmlFor={"socialAgeConfirmation-" + googleButtonId} className="check_area">
                    <div className="check_check_and_text_area">
                      <div className="check_box">
                        <div className="check_checked_box"></div>
                      </div>
                      <div className="check_text">
                        {__(
                          "I declare that I am 18 years or older, located in a permitted territory and have no self-exclusions."
                        )}
                        <span className="requried_icon">*</span>
                      </div>
                    </div>
                  </label>
                  <div className="errorMsg">{errors.agreeToTerms}</div>
                </div>
              </div>

              <div className="login_button">
                <Button onClick={registrationAction} className="commonBtn pTwoBtn">
                  {__("Confirm")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showOrDivider ? (
        <div className="or_text_section ">
          <div className="dash"></div>
          <div className="text">{__("or log in with")}</div>
          <div className="dash"></div>
        </div>
      ) : null}
    </>
  );
};

export default SocialLogin;
